import { Controller } from "@hotwired/stimulus";

/**
 * Stimulus controller for the live reporting sidebar navigation.
 * Ensures the selected nav item is visible and keeps the sidebar in sync with DOM changes.
 *
 * @class LiveReportingSidebarController
 * @extends Controller
 */
export default class extends Controller {
  /**
   * Called when the controller is connected to the DOM.
   * Scrolls to the selected nav item and sets up DOM mutation observation.
   */
  connect() {
    console.log('Live Reporting Sidebar connected');
    this.scrollToSelected();
    this.observeDomChanges();
  }

  /**
   * Called when the controller is disconnected from the DOM.
   * Cleans up the MutationObserver.
   */
  disconnect() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  /**
   * Scrolls the sidebar so the selected nav item is visible.
   * If #live-controls exists, accounts for its position.
   * Only scrolls if the selected item is not fully visible.
   */
  scrollToSelected() {
    const container = this.element;
    const liveControls = container.querySelector("#live-controls");
    const selectedItem = container.querySelector(".nav-item--selected");

    if (selectedItem) {
      const containerRect = container.getBoundingClientRect();

      if (liveControls) {
        // If liveControls exists, scroll so selected item is visible below it
        const liveControlsRect = liveControls.getBoundingClientRect();
        const selectedItemRect = selectedItem.getBoundingClientRect();
        const isFullyVisible =
          selectedItemRect.top >= containerRect.top &&
          selectedItemRect.bottom <= containerRect.bottom;
        if (!isFullyVisible) {
          container.scrollTop += selectedItemRect.top - liveControlsRect.top - containerRect.top;
        }
      } else {
        // If liveControls doesn't exist, just scroll to make the selected item visible
        const selectedItemRect = selectedItem.getBoundingClientRect();
        const isFullyVisible =
          selectedItemRect.top >= containerRect.top &&
          selectedItemRect.bottom <= containerRect.bottom;
        if (!isFullyVisible) {
          container.scrollTop += selectedItemRect.top - containerRect.top;
        }
      }
    }
  }

  /**
   * Observes DOM changes in the sidebar and re-scrolls to the selected item if needed.
   * Uses MutationObserver to watch for child or subtree changes.
   */
  observeDomChanges() {
    const container = this.element;
    this.mutationObserver = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        // Only re-scroll if the DOM structure changes
        if (mutation.type === "childList" || mutation.type === "subtree") {
          this.scrollToSelected();
          break;
        }
      }
    });
    this.mutationObserver.observe(container, {
      childList: true, // Watch for direct children changes
      subtree: true,   // Watch for changes in descendants
    });
  }
}